import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import Color from "color";
// import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import WithResponsiveRendering from "@/modules/common/components/wrappers/with-responsive-rendering";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import useUpdateTrackStatus from "../hooks/use-update-track-status.hook";
import { DeveloperTrack } from "../types";
import { Status } from "@/modules/common/dtos/index.v2";
import { FormFactory } from "@/modules/lead-page/components/lead-info/edit-fields/status/forms/form-factory/form-factory";

interface UpdateStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  track: DeveloperTrack | null;
  // Optionally pass hook values from parent for better state management
  hookValues?: ReturnType<typeof useUpdateTrackStatus>;
}

const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  isOpen,
  onClose,
  track,
  hookValues,
}) => {
  // Use provided hook values or create internal state
  const internalHook = useUpdateTrackStatus({ track });
  const {
    selectedStatusUId,
    setSelectedStatus,
    comment,
    setComment,
    statuses,
    statusesLoading,
    handleSubmit,
    isSubmitting,
    isValid,
    formFields,
    isLoadingForm,
  } = hookValues || internalHook;

  // Find selected status object
  const selectedStatus = statuses.find((s) => s.uid === selectedStatusUId);

  const handleConfirm = async () => {
    await handleSubmit();
  };

  // Adapter for FormFactory submit
  const handleFormFactorySubmit = async (values: any) => {
    // FormFactory returns all form values including comment
    // We pass it to our handleSubmit which now accepts formData
    await handleSubmit(values);
  };

  const ResponsiveModal = useMemo(() => WithResponsiveRendering({
    MobileComponent: CommonModal,
    DesktopComponent: CommonModal,
  }), []);

  return (
    <ResponsiveModal
      open={isOpen}
      onClose={onClose}
      title="Update track status"
      confirmButtonText={formFields?.data ? undefined : "Update"}
      cancelButtonText={formFields?.data ? undefined : "Cancel"}
      onConfirm={handleConfirm}
      isLoading={isSubmitting}
      isConfirmDisabled={!isValid}
      sx={{
        width: "480px",
        maxWidth: "90vw",
      }}
    >
      <Box
        sx={{
          padding: nawyTheme.nawy.spacing.md,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: nawyTheme.nawy.spacing.md,
        }}
      >
        {track && (
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#636363",
              }}
            >
              Updating status for{" "}
              <Typography
                component="span"
                sx={{
                  fontWeight: 600,
                  color: "#313131",
                }}
              >
                {track.developerName}
              </Typography>{" "}
              track
            </Typography>
          </Box>
        )}

        {/* Status Selector */}
        <Box>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: nawyTheme.nawy.colors.semantic.text.secondary,
              marginBottom: nawyTheme.nawy.spacing.xxs,
            }}
          >
            Status
          </Typography>
          <FormControl sx={{ width: "100%" }}>
            <Autocomplete
              value={selectedStatus || null}
              onChange={(_, newValue) => {
                setSelectedStatus(newValue?.uid || null, newValue?.name || "");
              }}
              options={statuses}
              loading={statusesLoading}
              getOptionLabel={(option) => option.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select status"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => {
                const colorObj = Color(option.color || "#ffffff");
                const backgroundColor = colorObj.alpha(0.3).toString();
                const textColor = option.color
                  ? colorObj.darken(0.5).toString()
                  : "#313131";

                return (
                  <li
                    {...props}
                    key={option.uid}
                    style={{
                      backgroundColor: backgroundColor,
                      color: textColor,
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    {option.name}
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => option.uid === value?.uid}
              sx={{ width: "100%" }}
            />
          </FormControl>
        </Box>

        {isLoadingForm ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : formFields?.data ? (
          <Box>
             <FormFactory
               submitFun={handleFormFactorySubmit}
               formFields={formFields.data}
               isLoadingSubmition={isSubmitting}
               handleClose={onClose}
               selectedStatus={selectedStatus as Status}
               developerTrackConfig={{
                 createdTracks: [],
                 developers: [],
                 isLoadingDevelopers: false,
                 setSearchDevelopers: () => {},
                 handleAddTrack: () => {},
                 handleDeleteTrack: () => {},
                 handleTrackChange: () => {},
               }}
               showComment={true}
             />
          </Box>
        ) : (
          /* Comment Field (Only shown if no dynamic form) */
          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                color: nawyTheme.nawy.colors.semantic.text.secondary,
                marginBottom: nawyTheme.nawy.spacing.xxs,
              }}
            >
              Comment (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder="Add a comment about this status change..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Box>
        )}
      </Box>
    </ResponsiveModal>
  );
};

export default UpdateStatusModal;

